import { useEffect, useState } from 'react'
import {
  subscribeToEffects,
  subscribeToEstateTypes,
  subscribeToGroups,
  subscribeToHints,
  subscribeToOptions,
  subscribeToQuestions,
  subscribeToSectionTypes,
} from '@/features/rules/rules-api'
import type {
  ConfigQuestion,
  EstateType,
  MaterialEffect,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
} from '@/lib/types'

export type RulesState = {
  estateTypes: EstateType[]
  sectionTypes: SectionType[]
  groups: QuestionGroup[]
  hints: QuestionHint[]
  questions: ConfigQuestion[]
  options: QuestionOption[]
  effects: MaterialEffect[]
  isLoading: boolean
  error: string | null
}

/**
 * Subscribes to the whole rules tree at once. The seven collections are small and the editor needs
 * them together (a question shows its options, effects and the groups it can belong to), so this is
 * one hook instead of seven — and every screen filters the arrays in memory.
 */
export function useRules(): RulesState {
  const [estateTypes, setEstateTypes] = useState<EstateType[]>([])
  const [sectionTypes, setSectionTypes] = useState<SectionType[]>([])
  const [groups, setGroups] = useState<QuestionGroup[]>([])
  const [hints, setHints] = useState<QuestionHint[]>([])
  const [questions, setQuestions] = useState<ConfigQuestion[]>([])
  const [options, setOptions] = useState<QuestionOption[]>([])
  const [effects, setEffects] = useState<MaterialEffect[]>([])
  const [loaded, setLoaded] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const done = () => setLoaded((value) => value + 1)
    const fail = (caught: Error) => {
      done()
      setError(
        caught.message.includes('permission')
          ? 'Firestore отклонил чтение конфигурации правил. Проверьте, что правила опубликованы и у вас роль admin.'
          : caught.message,
      )
    }
    // `loaded` counts the collections that have answered at least once; the page shows skeletons
    // until all seven have.
    const once = <T,>(setter: (items: T[]) => void) => {
      let first = true
      return (items: T[]) => {
        setter(items)
        if (first) {
          first = false
          done()
        }
      }
    }

    const unsubscribes = [
      subscribeToEstateTypes(once(setEstateTypes), fail),
      subscribeToSectionTypes(once(setSectionTypes), fail),
      subscribeToGroups(once(setGroups), fail),
      subscribeToHints(once(setHints), fail),
      subscribeToQuestions(once(setQuestions), fail),
      subscribeToOptions(once(setOptions), fail),
      subscribeToEffects(once(setEffects), fail),
    ]
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe()
    }
  }, [])

  return {
    estateTypes,
    sectionTypes,
    groups,
    hints,
    questions,
    options,
    effects,
    isLoading: loaded < 7,
    error,
  }
}
